import React, { useState, useEffect, useRef } from 'react';
import { Building2, Shield, Headphones, HeartPulse, ShoppingCart, Zap, Truck, Landmark } from 'lucide-react';
import content from '../../translations/content.json';
import './style.css';

const iconComponents = {
  Building2: Building2,
  Shield: Shield,
  Headphones: Headphones,
  HeartPulse: HeartPulse,
  ShoppingCart: ShoppingCart,
  Zap: Zap,
  Truck: Truck,
  Landmark: Landmark
};

const IndustriesGrid = () => {
  const [visibleCards, setVisibleCards] = useState({});
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => ({
              ...prev,
              [entry.target.dataset.index]: true,
            }));
            observer.unobserve(entry.target); // Stop observing once visible
          }
        });
      },
      { threshold: 0.1 } // Trigger when 10% of the item is visible
    );

    cardRefs.current.forEach((cardRef) => {
      if (cardRef) {
        observer.observe(cardRef);
      }
    });

    return () => {
      cardRefs.current.forEach((cardRef) => {
        if (cardRef) {
          observer.unobserve(cardRef);
        }
      });
    };
  }, []);

  return (
    <section className="industries-grid-section">
      <div className="industries-grid-container">
        <div className="industries-grid-header">

          <div className="industries-grid-header-text">
            <div className="industries-grid-subtitle-container">
              <span className="text-sm">::</span>
              <span>{content.industries.subtitle}</span>
            </div>
            <h2 className="industries-grid-heading">
              {content.industries.heading.split('<br />').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < content.industries.heading.split('<br />').length - 1 && <br />}
                </React.Fragment>
              ))}
            </h2>
          </div>

          <div className="industries-grid">
            {content.industries.list.map((item, idx) => {
              const Icon = iconComponents[item.icon];
              const colors = [
                "bg-purple-50/80 border-purple-100 text-[#5E17EB]",
                "bg-indigo-50/80 border-indigo-100 text-[#4338CA]",
                "bg-pink-50/80 border-pink-100 text-[#EC4899]",
                "bg-emerald-50/80 border-emerald-100 text-[#10B981]",
                "bg-orange-50/80 border-orange-100 text-[#FF6B00]",
                "bg-violet-50/80 border-violet-100 text-[#7C3AED]",
                "bg-purple-50/80 border-purple-100 text-[#6D28D9]",
                "bg-blue-50/80 border-blue-100 text-[#2563EB]"
              ];
              const itemColorClass = colors[idx % colors.length];

              return (
                <div
                  key={idx}
                  ref={(el) => (cardRefs.current[idx] = el)}
                  data-index={idx}
                  data-testid={`industry-card-${idx}`}
                  className={`industry-card ${visibleCards[idx] ? 'card-visible' : 'card-hidden'}`}
                >
                  <div className={`industry-card-icon-container ${itemColorClass}`}>
                    {Icon && <Icon className="industry-card-icon"/>}
                  </div>
                  <span className="industry-card-name">
                    {item.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustriesGrid;
